"use client"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"
import { ChevronRight } from "lucide-react"
import { useForm } from "react-hook-form"
import { createClient } from "@/lib/supabase/client"
import { validatePassword } from "@/lib/auth/password-policy"
import { GoogleAuthButton } from "@/components/auth/google-auth-button"
import { AppleAuthButton } from "@/components/auth/apple-auth-button"
import { AuthPageShell, authFieldClass, authPillGradient } from "@/components/auth/auth-page-shell"
import { trackMeta } from "@/lib/meta-pixel"
import { trackTikTok } from "@/lib/tiktok-pixel"
import { claimAttribution } from "@/lib/attribution-client"
import { newSignupEventId, reportSignupToMeta } from "@/lib/meta-capi-client"
import { Lozenge } from "@/components/ui/lozenge"
import { SmsConsent } from "@/components/sms-consent"

const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const EyeOffIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
)

type SignupRole = "barber" | "owner" | "client"

type SignupFormValues = {
  email: string
  password: string
  firstName: string
  lastName: string
  phone: string
}

export default function SignupPage() {
  const t = useTranslations("pages.auth.signup")
  const tPolicy = useTranslations("pages.auth.passwordPolicy")
  const router = useRouter()
  const searchParams = useSearchParams()
  const inviteToken = searchParams.get("invite")
  const roleParam = searchParams.get("role") as SignupRole | null
  const planParam = searchParams.get("plan")
  // Referral attribution — a barber's invite link carries ?ref=<their user id>.
  const refParam = searchParams.get("ref")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  // Role lives outside the form so the Google button (above the form) and the
  // email/password submit share one selection. Invited barbers are locked to
  // "barber" — the picker is hidden for them.
  const [role, setRole] = useState<SignupRole>(roleParam || "barber")
  const effectiveRole: SignupRole = inviteToken ? "barber" : role

  // When arriving from a shop invite link (?invite=<token>), pull the shop's
  // name + logo so the signup is co-branded ("Onboard to <Shop>"). A pending
  // invitation is publicly readable by token (RLS), so the anon client can
  // fetch it before the user has an account.
  const [inviteShop, setInviteShop] = useState<{ name: string; cover_image: string | null } | null>(null)
  useEffect(() => {
    if (!inviteToken) return
    let cancelled = false
    const supabase = createClient()
    supabase
      .from("barber_invitations")
      .select("shop:shops(name, cover_image)")
      .eq("invite_token", inviteToken)
      .eq("status", "pending")
      .gt("expires_at", new Date().toISOString())
      .maybeSingle()
      .then(({ data }: { data: { shop: unknown } | null }) => {
        if (cancelled) return
        const shop = (data?.shop as { name?: string; cover_image?: string | null } | null) ?? null
        if (shop?.name) setInviteShop({ name: shop.name, cover_image: shop.cover_image ?? null })
      })
    return () => {
      cancelled = true
    }
  }, [inviteToken])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormValues>()

  async function onSubmit(data: SignupFormValues) {
    setLoading(true)
    setErrorMessage("")

    try {
      // Step 1: Create user via server API (admin — no confirmation email needed)
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          role: effectiveRole,
          selectedPlan: effectiveRole === "owner" ? planParam : undefined,
          referredBy: refParam || undefined,
          // The invite travels with the signup itself. localStorage below is
          // now the fallback, not the mechanism: an invitation with no email
          // on it (every link invite) has no second key, so if the token only
          // lived in the browser and onboarding was abandoned, the barber
          // never joined the shop at all.
          inviteToken: inviteToken || undefined,
        }),
      })

      const result = await res.json()

      if (!res.ok) {
        setErrorMessage(result.error || t("createFailed"))
        setLoading(false)
        return
      }

      // Meta conversion event — see components/auth/auth-modal.tsx for why
      // this fires before the redirect rather than after sign-in.
      // One id, shared with the server event below, so Meta counts this
      // signup once rather than twice.
      const metaEventId = newSignupEventId()
      trackMeta("CompleteRegistration", { content_category: effectiveRole, status: true }, metaEventId)
      trackTikTok("CompleteRegistration", { content_type: "product", content_id: effectiveRole })

      // Step 2: Sign in immediately with the new credentials
      const supabase = createClient()
      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      })

      if (signInError) {
        // Account was created but sign-in failed — send them to login
        router.push(`/auth/login?message=${encodeURIComponent(t("accountCreated"))}`)
        return
      }

      // Attribution is claimed here, not next to the pixel above: the pixel
      // deliberately fires before sign-in, and the claim route reads the
      // caller's session to decide whose row to write.
      claimAttribution()
      reportSignupToMeta(metaEventId)

      if (authData.user) {
        if (inviteToken) {
          localStorage.setItem("booth_pending_invite", inviteToken)
        }
        // Clients skip the pro onboarding (shop/plan setup) — straight to their
        // dashboard. Pros continue to onboarding.
        router.push(effectiveRole === "client" ? "/dashboard/client" : "/auth/onboarding")
      }
    } catch (err) {
      console.error("Unexpected signup error:", err)
      setErrorMessage(t("unexpectedError"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthPageShell>
        <div className="space-y-5">
          <div className="space-y-4 text-center">
            <div className="flex items-center justify-center">
              <Link href="/" className="flex items-center justify-center">
                <Image src="/images/booth-icon-white.png" alt="BOOTH" width={32} height={21} priority />
              </Link>
            </div>

            {inviteShop ? (
              // Shop-branded invite banner — the barber sees whose team they're
              // joining before they fill anything in.
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-5">
                {inviteShop.cover_image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={inviteShop.cover_image}
                    alt={inviteShop.name}
                    className="h-14 w-14 rounded-2xl object-cover ring-1 ring-white/10"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-lg font-bold text-white ring-1 ring-white/10">
                    {inviteShop.name.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="space-y-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-400">
                    You've been invited
                  </p>
                  <h1 className="text-lg font-bold leading-tight text-white">Onboard to {inviteShop.name}</h1>
                  <p className="text-xs text-neutral-400">Create your account below to join the team.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2.5">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-medium text-neutral-300">
                  <Lozenge className="w-2.5" />
                  {t("eyebrow")}
                </span>
                <h1 className="text-xl font-semibold tracking-tight text-white">{t("heading")}</h1>
              </div>
            )}
          </div>

          {errorMessage && (
            <div className="rounded-2xl border border-red-500/15 bg-red-500/[0.06] p-3 text-[11px] text-red-400">
              {errorMessage}
            </div>
          )}

          {!inviteToken && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span
                  aria-hidden
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] text-[9px] font-semibold leading-none text-neutral-400"
                >
                  1
                </span>
                <span id="signup-step-role" className="text-[11px] font-medium text-neutral-400">
                  {t("step1")}
                </span>
              </div>
              <div role="radiogroup" aria-labelledby="signup-step-role" className="grid grid-cols-3 gap-2">
              {(
                [
                  { value: "barber", label: t("roleBarber") },
                  { value: "owner", label: t("roleOwner") },
                  { value: "client", label: "Client" },
                ] as const
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={role === option.value}
                  onClick={() => setRole(option.value)}
                  style={role === option.value ? authPillGradient : undefined}
                  className={
                    role === option.value
                      ? "flex h-9 items-center justify-center rounded-full border border-white/15 text-xs font-medium text-white shadow-sm transition-all"
                      : "flex h-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.02] text-xs font-medium text-neutral-500 transition-all hover:text-neutral-300"
                  }
                >
                  {option.label}
                </button>
              ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            {!inviteToken && (
              <div className="flex items-center gap-2">
                <span
                  aria-hidden
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] text-[9px] font-semibold leading-none text-neutral-400"
                >
                  2
                </span>
                <span id="signup-step-account" className="text-[11px] font-medium text-neutral-400">
                  {t("step2")}
                </span>
              </div>
            )}
            <AppleAuthButton
              label={t("appleCta")}
              errorFallback={t("appleError")}
              role={effectiveRole}
              inviteToken={inviteToken}
              onError={setErrorMessage}
            />
            <GoogleAuthButton
              label={t("googleCta")}
              errorFallback={t("googleError")}
              role={effectiveRole}
              inviteToken={inviteToken}
              onError={setErrorMessage}
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] tracking-[0.3em] text-neutral-600">{t("or")}</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label htmlFor="firstName" className="sr-only">
                  {t("firstNameLabel")}
                </label>
                <input
                  id="firstName"
                  type="text"
                  placeholder={t("firstNameLabel")}
                  className={authFieldClass}
                  {...register("firstName", { required: t("firstNameRequired") })}
                />
                {errors.firstName && <p className="text-[11px] text-red-400">{errors.firstName.message}</p>}
              </div>
              <div className="space-y-1">
                <label htmlFor="lastName" className="sr-only">
                  {t("lastNameLabel")}
                </label>
                <input
                  id="lastName"
                  type="text"
                  placeholder={t("lastNameLabel")}
                  className={authFieldClass}
                  {...register("lastName", { required: t("lastNameRequired") })}
                />
                {errors.lastName && <p className="text-[11px] text-red-400">{errors.lastName.message}</p>}
              </div>
            </div>

            <div className="space-y-1">
              <label htmlFor="email" className="sr-only">
                {t("emailLabel")}
              </label>
              <input
                id="email"
                type="email"
                placeholder={t("emailLabel")}
                className={authFieldClass}
                {...register("email", {
                  required: t("emailRequired"),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("emailInvalid"),
                  },
                })}
              />
              {errors.email && <p className="text-[11px] text-red-400">{errors.email.message}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="phone" className="sr-only">
                {t("phoneLabel")}
              </label>
              <input
                id="phone"
                type="tel"
                placeholder={t("phoneLabel")}
                className={authFieldClass}
                {...register("phone", { required: t("phoneRequired") })}
              />
              {errors.phone && <p className="text-[11px] text-red-400">{errors.phone.message}</p>}
              <SmsConsent tone="dark" className="px-1" />
            </div>

            <div className="space-y-1">
              <label htmlFor="password" className="sr-only">
                {t("passwordLabel")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("passwordLabel")}
                  className={`${authFieldClass} pr-12`}
                  {...register("password", {
                    required: t("passwordRequired"),
                    validate: (pw) => {
                      const result = validatePassword(pw)
                      return result.ok ? true : tPolicy(result.code)
                    },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 transition-colors hover:text-white"
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {errors.password ? (
                <p className="text-[11px] text-red-400">{errors.password.message}</p>
              ) : (
                <p className="text-[10px] text-neutral-600">{tPolicy("requirements")}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              style={authPillGradient}
              className="flex h-10 w-full items-center justify-center gap-1.5 rounded-full border border-white/10 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {loading ? t("submitting") : t("submit")}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </form>

          <p className="text-center text-[11px] text-neutral-400">
            {t("haveAccount")}{" "}
            <Link
              href={inviteToken ? `/auth/login?invite=${inviteToken}` : "/auth/login"}
              className="font-semibold text-white hover:underline"
            >
              {t("signIn")}
            </Link>
          </p>

          <p className="text-center text-[11px] text-neutral-500">
            {t("footerPrefix")}{" "}
            <Link href="/terms" className="text-neutral-300 hover:text-white">
              {t("terms")}
            </Link>{" "}
            {t("and")}{" "}
            <Link href="/privacy" className="text-neutral-300 hover:text-white">
              {t("privacy")}
            </Link>
          </p>
        </div>
    </AuthPageShell>
  )
}
